//core
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { commonAdminService } from "../../../services/admin/commonAdmin.service";
import { tagsService } from "../../../services/admin/tags.service";
import { setUser } from "../../common/user";
import {
  setCreateCreativeState,
  setGeneralProgressHide,
  setGeneralProgressShow,
  setGeneralSnackbarState
} from "../../common/ui";

export type CommonAdminSliceState = {
  locales: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  tags: {
    list: {
      data: any | null,
      error: any,
      isFetching: boolean,
    },
    create: {
      data: any | null,
      error: any,
      isFetching: boolean,
    },
  },
};

const initialState: CommonAdminSliceState = {
  locales: {
    data: null,
    error: null,
    isFetching: false,
  },
  tags: {
    list: {
      data: null,
      error: null,
      isFetching: false,
    },
    create: {
      data: null,
      error: null,
      isFetching: false,
    },
  },
};

export const getLocalesData: any = createAsyncThunk(
  'adm/common/getLocalesData',
  async (payload:any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await commonAdminService.getLocales(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getTagsListData: any = createAsyncThunk(
  'adm/common/getTagsList',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await tagsService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createTagData: any = createAsyncThunk(
  'adm/common/createTag',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await tagsService.create(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data);
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      return rejectWithValue(error);
    }
  }
);

//slice
const commonAdminSlice = createSlice({
  name: 'adm/common',
  initialState: initialState,
  reducers: {
    clearLocalesState(state) {
      state.locales.data = null;
      state.locales.error = null;
      state.locales.isFetching = false;
    },
    clearTagsListState(state) {
      state.tags.list.data = null;
      state.tags.list.error = null;
      state.tags.list.isFetching = false;
    },
    clearTagsCreateState(state) {
      state.tags.create.data = null;
      state.tags.create.error = null;
      state.tags.create.isFetching = false;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<CommonAdminSliceState>>) => {
    builder.addCase(getLocalesData.pending, (state ) => {
      state.locales.isFetching = true;
      state.locales.error = null;
    });
    builder.addCase(getLocalesData.fulfilled, (state , action) => {
      state.locales.error = null;
      state.locales.isFetching = false;
      state.locales.data = action.payload;
    });
    builder.addCase(getLocalesData.rejected, (state , action) => {
      state.locales.isFetching = false;
      state.locales.error = action.payload;
    });

    builder.addCase(getTagsListData.pending, (state) => {
      state.tags.list.isFetching = true;
      state.tags.list.error = null;
    });
    builder.addCase(getTagsListData.fulfilled, (state, action) => {
      state.tags.list.error = null;
      state.tags.list.isFetching = false;
      state.tags.list.data = action.payload;
    });
    builder.addCase(getTagsListData.rejected, (state, action) => {
      state.tags.list.isFetching = false;
      state.tags.list.error = action.payload;
    });

    builder.addCase(createTagData.pending, (state) => {
      state.tags.create.isFetching = true;
      state.tags.create.error = null;
    });
    builder.addCase(createTagData.fulfilled, (state, action) => {
      state.tags.create.error = null;
      state.tags.create.isFetching = false;
      state.tags.create.data = action.payload;
    });
    builder.addCase(createTagData.rejected, (state, action) => {
      state.tags.create.isFetching = false;
      state.tags.create.error = action.payload;
    });
  },
});

export default commonAdminSlice.reducer;

export const {
  clearLocalesState,
  clearTagsListState,
  clearTagsCreateState,
} = commonAdminSlice.actions;
